import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import Header from '../Common/Header';
import { api } from '../../services/api';
import './SurveyResults.css';


ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);


const SurveyResults = () => {
  const { t } = useTranslation();
  const [surveyData, setSurveyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState('concerns');
  const [isRealData, setIsRealData] = useState(false);
  const [totalResponses, setTotalResponses] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);


  useEffect(() => {
    loadSurveyData();
  }, []);


  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadSurveyData();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh]);


  const loadSurveyData = async () => {
    try {
      console.log('📊 Fetching live survey data...');

      const reportRes = await api.getSurveyReport();

      if (reportRes?.data) {
        const data = reportRes.data;
        setSurveyData(data);
        setTotalResponses(data.totalSurveys || 0);
        setIsRealData((data.totalSurveys || 0) >= 5);
        setLastUpdated(new Date().toLocaleTimeString());

        console.log('✅ Survey data loaded:', data);
      }

      setLoading(false);
    } catch (error) {
      console.error('❌ Error loading survey data:', error);
      setLoading(false);
    }
  };


  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top' },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed.y || context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${value.toLocaleString()} ${t('surveyResults.responses') || 'responses'} (${percentage}%)`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => value.toLocaleString()
        }
      }
    }
  };


  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { padding: 20, font: { size: 12, weight: 'bold' } }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${context.label}: ${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    }
  };


  if (loading) {
    return (
      <>
        <Header />
        <div className="survey-results-page">
          <div className="loading-container">
            <div className="spinner-large"></div>
            <p>{t('common.loading') || 'Loading live survey data...'}</p>
          </div>
        </div>
      </>
    );
  }


  const getMainConcernsChart = () => ({
    labels: surveyData?.concerns?.map((c) => c.concern) || [],
    datasets: [
      {
        label: t('surveyResults.responses') || 'Responses',
        data: surveyData?.concerns?.map((c) => c.count) || [],
        backgroundColor: [
          'rgba(102, 126, 234, 0.8)',
          'rgba(118, 75, 162, 0.8)',
          'rgba(240, 147, 251, 0.8)',
          'rgba(79, 172, 254, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(156, 163, 175, 0.8)'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  });


  const getTopPrioritiesChart = () => ({
    labels: surveyData?.priorities?.map((p) => p.priority) || [],
    datasets: [
      {
        label: t('surveyResults.votes') || 'Votes',
        data: surveyData?.priorities?.map((p) => p.count) || [],
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 2
      }
    ]
  });


  const getGovernmentRatingChart = () => ({
    labels: surveyData?.performance?.map((r) => r.rating) || [],
    datasets: [
      {
        data: surveyData?.performance?.map((r) => r.count) || [],
        backgroundColor: [
          'rgba(16, 185, 129, 0.8)',
          'rgba(79, 172, 254, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(127, 29, 29, 0.8)'
        ],
        borderWidth: 3,
        borderColor: '#fff'
      }
    ]
  });


  const getMostImportantIssuesChart = () => ({
    labels: surveyData?.issues?.map((i) => i.issue) || [],
    datasets: [
      {
        label: t('surveyResults.responses') || 'Responses',
        data: surveyData?.issues?.map((i) => i.count) || [],
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderColor: 'rgba(239, 68, 68, 1)',
        borderWidth: 2
      }
    ]
  });


  const getCandidatesChart = () => ({
    labels: surveyData?.candidates?.map((c) => c.candidate) || [],
    datasets: [
      {
        label: t('surveyResults.votes') || 'Votes',
        data: surveyData?.candidates?.map((c) => c.votes) || [],
        backgroundColor: 'rgba(102, 126, 234, 0.8)',
        borderColor: 'rgba(102, 126, 234, 1)',
        borderWidth: 2
      }
    ]
  });


  const getCMPreferencesChart = () => ({
    labels: surveyData?.cmPreferences?.map((cm) => cm.chiefMinister) || [],
    datasets: [
      {
        data: surveyData?.cmPreferences?.map((cm) => cm.votes) || [],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(168, 85, 247, 0.8)',
          'rgba(236, 72, 153, 0.8)',
          'rgba(249, 115, 22, 0.8)',
          'rgba(156, 163, 175, 0.8)'
        ],
        borderWidth: 3,
        borderColor: '#fff'
      }
    ]
  });


  return (
    <>
      <Header />

      <div className="survey-results-page">
        <div className="survey-results-container">
          {/* Page Header */}
          <div className="results-header">
            <h1>
              <i className="fas fa-chart-line" aria-hidden="true"></i> 
              {t('surveyResults.title') || 'Live Survey Results & Analytics'}
            </h1>
            <p>
              {isRealData
                ? `${t('surveyResults.realTimeData') || 'Real-time data'} • ${(surveyData?.totalSurveys || 0).toLocaleString()} ${t('surveyResults.responsesCollected') || 'responses collected'}`
                : t('surveyResults.noResponses') || 'No responses yet - Be the first to take the survey!'}
            </p>
          </div>


          {/* Live Data Badge */}
          {isRealData && (
            <div className="live-badge">
              <i className="fas fa-circle live-dot" aria-hidden="true"></i>
              <span>
                {t('surveyResults.liveData') || 'Live Data'} • {t('surveyResults.updated') || 'Updated'}: {lastUpdated}
              </span>
              <button
                className="refresh-btn"
                onClick={() => loadSurveyData()}
                title={t('common.refresh') || 'Refresh data'}
              >
                <i className="fas fa-sync-alt" aria-hidden="true"></i>
              </button>
              <button
                className="auto-refresh-btn"
                onClick={() => setAutoRefresh(!autoRefresh)}
                title={autoRefresh ? t('surveyResults.disableAutoRefresh') : t('surveyResults.enableAutoRefresh')}
              >
                <i className={`fas fa-${autoRefresh ? 'pause' : 'play'}`} aria-hidden="true"></i>
              </button>
            </div>
          )}


          {/* No Data Yet */}
          {!isRealData && totalResponses === 0 && (
            <div className="no-data-state">
              <i className="fas fa-chart-line" aria-hidden="true"></i>
              <h3>{t('surveyResults.noResponsesYet') || 'No Responses Yet'}</h3>
              <p>{t('surveyResults.beFirstSurvey') || 'Be the first to share your opinion in our survey!'}</p>
              <a href="/survey" className="participate-cta">
                <i className="fas fa-vote-yea" aria-hidden="true"></i> 
                {t('surveyResults.takeSurveyNow') || 'Take Survey Now'}
              </a>
            </div>
          )}


          {/* Summary Stats */}
          {isRealData && surveyData && (
            <>
              <div className="summary-stats">
                <div className="stat-box">
                  <i className="fas fa-users" aria-hidden="true"></i>
                  <div className="stat-content">
                    <h3>{(surveyData?.totalSurveys || 0).toLocaleString()}</h3>
                    <p>
                      {t('surveyResults.totalSurveys') || 'Total Surveys'} 
                      <span className="live-tag">{t('common.live') || 'LIVE'}</span>
                    </p>
                  </div>
                </div>
                <div className="stat-box">
                  <i className="fas fa-mars" aria-hidden="true"></i>
                  <div className="stat-content">
                    <h3>{(surveyData?.stats?.maleCount || 0).toLocaleString()}</h3>
                    <p>{t('surveyResults.maleRespondents') || 'Male Respondents'}</p>
                  </div>
                </div>
                <div className="stat-box">
                  <i className="fas fa-venus" aria-hidden="true"></i>
                  <div className="stat-content">
                    <h3>{(surveyData?.stats?.femaleCount || 0).toLocaleString()}</h3>
                    <p>{t('surveyResults.femaleRespondents') || 'Female Respondents'}</p>
                  </div>
                </div>
              </div>


              {/* View Selector */}
              <div className="view-selector">
                <button
                  className={`view-btn ${selectedView === 'concerns' ? 'active' : ''}`}
                  onClick={() => setSelectedView('concerns')}
                  title={t('surveyResults.concerns') || 'Concerns'}
                >
                  <i className="fas fa-exclamation-circle" aria-hidden="true"></i> 
                  {t('surveyResults.concerns') || 'Concerns'}
                </button>
                <button
                  className={`view-btn ${selectedView === 'priorities' ? 'active' : ''}`}
                  onClick={() => setSelectedView('priorities')}
                  title={t('surveyResults.priorities') || 'Priorities'}
                >
                  <i className="fas fa-flag" aria-hidden="true"></i> 
                  {t('surveyResults.priorities') || 'Priorities'}
                </button>
                <button
                  className={`view-btn ${selectedView === 'issues' ? 'active' : ''}`}
                  onClick={() => setSelectedView('issues')}
                  title={t('surveyResults.issues') || 'Issues'}
                >
                  <i className="fas fa-fire" aria-hidden="true"></i> 
                  {t('surveyResults.issues') || 'Issues'}
                </button>
                <button
                  className={`view-btn ${selectedView === 'rating' ? 'active' : ''}`}
                  onClick={() => setSelectedView('rating')}
                  title={t('surveyResults.rating') || 'Rating'}
                >
                  <i className="fas fa-star" aria-hidden="true"></i> 
                  {t('surveyResults.rating') || 'Rating'}
                </button>
                <button
                  className={`view-btn ${selectedView === 'candidates' ? 'active' : ''}`}
                  onClick={() => setSelectedView('candidates')}
                  title={t('surveyResults.candidates') || 'Candidates'}
                >
                  <i className="fas fa-user" aria-hidden="true"></i> 
                  {t('surveyResults.candidates') || 'Candidates'}
                </button>
                <button
                  className={`view-btn ${selectedView === 'cm' ? 'active' : ''}`}
                  onClick={() => setSelectedView('cm')}
                  title={t('surveyResults.cm') || 'CM'}
                >
                  <i className="fas fa-crown" aria-hidden="true"></i> 
                  {t('surveyResults.cm') || 'CM'}
                </button>
              </div>


              {/* Main Concerns */}
              {selectedView === 'concerns' && surveyData?.concerns?.length > 0 && (
                <div className="chart-section">
                  <div className="chart-header">
                    <h2>{t('surveyResults.mainVoterConcerns') || 'Main Voter Concerns'}</h2>
                    <p>{t('surveyResults.concernsDesc') || 'What issues matter most to Bihar voters'}</p>
                  </div>
                  <div className="chart-container" style={{ height: '400px' }}>
                    <Bar data={getMainConcernsChart()} options={chartOptions} />
                  </div>
                  <div className="data-table">
                    <table>
                      <thead>
                        <tr>
                          <th>{t('surveyResults.concern') || 'Concern'}</th>
                          <th>{t('surveyResults.responses') || 'Responses'}</th>
                          <th>{t('surveyResults.percentage') || 'Percentage'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {surveyData?.concerns?.map((concern, idx) => (
                          <tr key={idx}>
                            <td>{concern.concern}</td>
                            <td>{(concern.count || 0).toLocaleString()}</td>
                            <td>
                              <div className="percentage-bar">
                                <div
                                  className="bar-fill"
                                  style={{
                                    width: `${concern.percentage || 0}%`
                                  }}
                                ></div>
                                <span>{concern.percentage || 0}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}


              {/* Top Priorities */}
              {selectedView === 'priorities' && surveyData?.priorities?.length > 0 && (
                <div className="chart-section">
                  <div className="chart-header">
                    <h2>{t('surveyResults.topPriorities') || 'Top Development Priorities'}</h2>
                    <p>{t('surveyResults.prioritiesDesc') || 'What voters want prioritized'}</p>
                  </div>
                  <div className="chart-container" style={{ height: '400px' }}>
                    <Bar data={getTopPrioritiesChart()} options={chartOptions} />
                  </div>
                  <div className="data-table">
                    <table>
                      <thead>
                        <tr>
                          <th>{t('surveyResults.priority') || 'Priority'}</th>
                          <th>{t('surveyResults.responses') || 'Responses'}</th>
                          <th>{t('surveyResults.percentage') || 'Percentage'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {surveyData?.priorities?.map((priority, idx) => (
                          <tr key={idx}>
                            <td>{priority.priority}</td>
                            <td>{(priority.count || 0).toLocaleString()}</td>
                            <td>
                              <div className="percentage-bar">
                                <div
                                  className="bar-fill"
                                  style={{
                                    width: `${priority.percentage || 0}%`
                                  }}
                                ></div>
                                <span>{priority.percentage || 0}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}


              {/* Most Important Issues */}
              {selectedView === 'issues' && surveyData?.issues?.length > 0 && (
                <div className="chart-section">
                  <div className="chart-header">
                    <h2>{t('surveyResults.importantIssues') || 'Most Important Issues'}</h2>
                    <p>{t('surveyResults.issuesDesc') || 'Critical challenges facing Bihar'}</p>
                  </div>
                  <div className="chart-container" style={{ height: '400px' }}>
                    <Bar data={getMostImportantIssuesChart()} options={chartOptions} />
                  </div>
                  <div className="data-table">
                    <table>
                      <thead>
                        <tr>
                          <th>{t('surveyResults.issue') || 'Issue'}</th>
                          <th>{t('surveyResults.responses') || 'Responses'}</th>
                          <th>{t('surveyResults.percentage') || 'Percentage'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {surveyData?.issues?.map((issue, idx) => (
                          <tr key={idx}>
                            <td>{issue.issue}</td>
                            <td>{(issue.count || 0).toLocaleString()}</td>
                            <td>
                              <div className="percentage-bar">
                                <div
                                  className="bar-fill"
                                  style={{
                                    width: `${issue.percentage || 0}%`
                                  }}
                                ></div>
                                <span>{issue.percentage || 0}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}


              {/* Government Performance */}
              {selectedView === 'rating' && surveyData?.performance?.length > 0 && (
                <div className="chart-section">
                  <div className="chart-header">
                    <h2>{t('surveyResults.govRating') || 'Government Performance Rating'}</h2>
                    <p>{t('surveyResults.govRatingDesc') || 'How voters rate current government'}</p>
                  </div>
                  <div className="chart-container" style={{ height: '400px' }}>
                    <Doughnut data={getGovernmentRatingChart()} options={doughnutOptions} />
                  </div>
                </div>
              )}


              {/* Candidates */}
              {selectedView === 'candidates' && surveyData?.candidates?.length > 0 && (
                <div className="chart-section">
                  <div className="chart-header">
                    <h2>{t('surveyResults.topCandidates') || 'Top Candidates'}</h2>
                    <p>{t('surveyResults.candidatesDesc') || 'Voting preferences by candidate'}</p>
                  </div>
                  <div className="chart-container" style={{ height: '400px' }}>
                    <Bar data={getCandidatesChart()} options={chartOptions} />
                  </div>
                </div>
              )}


              {/* CM Preferences */}
              {selectedView === 'cm' && surveyData?.cmPreferences?.length > 0 && (
                <div className="chart-section">
                  <div className="chart-header">
                    <h2>{t('surveyResults.cmPreferences') || 'Chief Minister Preferences'}</h2>
                    <p>{t('surveyResults.cmChoice') || 'Voters\' CM choice'}</p>
                  </div>
                  <div className="chart-container" style={{ height: '400px' }}>
                    <Doughnut data={getCMPreferencesChart()} options={doughnutOptions} />
                  </div>
                </div>
              )}


              {/* Key Insights */}
              <div className="insights-section">
                <h2>
                  <i className="fas fa-lightbulb" aria-hidden="true"></i> 
                  {t('surveyResults.keyInsights') || 'Key Insights'}
                </h2>
                <div className="insights-grid">
                  <div className="insight-card">
                    <i className="fas fa-briefcase insight-icon" aria-hidden="true"></i>
                    <h3>{t('surveyResults.topConcern') || 'Top Concern'}</h3>
                    <p>
                      <strong>{surveyData?.concerns?.[0]?.percentage || 0}%</strong> 
                      {` ${t('surveyResults.votersCite') || 'voters cite'} `}
                      <em>{surveyData?.concerns?.[0]?.concern}</em> 
                      {` ${t('surveyResults.mainConcern') || 'as their main concern.'}`}
                    </p>
                  </div>
                  <div className="insight-card">
                    <i className="fas fa-road insight-icon" aria-hidden="true"></i>
                    <h3>{t('surveyResults.topPriority') || 'Top Priority'}</h3>
                    <p>
                      <strong>{surveyData?.priorities?.[0]?.percentage || 0}%</strong>
                      {` ${t('surveyResults.prioritize') || 'prioritize'} `}
                      <em>{surveyData?.priorities?.[0]?.priority}</em>.
                    </p>
                  </div>
                  <div className="insight-card">
                    <i className="fas fa-graduation-cap insight-icon" aria-hidden="true"></i>
                    <h3>{t('surveyResults.topIssue') || 'Top Issue'}</h3>
                    <p>
                      <strong>{surveyData?.issues?.[0]?.percentage || 0}%</strong>
                      {` ${t('surveyResults.see') || 'see'} `}
                      <em>{surveyData?.issues?.[0]?.issue}</em> 
                      {` ${t('surveyResults.mostImportant') || 'as most important.'}`}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}


          {/* Disclaimer */}
          <div className="disclaimer">
            <i className="fas fa-info-circle" aria-hidden="true"></i>
            <p>
              <strong>{t('surveyResults.liveDataLabel') || 'Live Data'}:</strong> 
              {` ${t('surveyResults.disclaimer') || 'This data updates every 30 seconds and reflects real voter survey responses submitted on this platform.'}`}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};


export default SurveyResults;
